<template>
    <div class="reservation-landing-page-builder">
        <div class="route-page-content breadcrumb-content">
            <SidebarChild
                :items="menuItems"
                :selected-item="currentPage"
                :focusActive="true"
                @selected="fnClicked"
                @clicked="fnClicked"
            />
            <div class="page-content-wrapper">
                <div v-if="loading" class="loading">{{ $t("message.loading") }}...</div>
                <div v-else-if="!data" class="no-data">
                    <SupsisButton @click="createMockLandingPage" class="create-button"
                        >{{ $t("calendar.setupBookingWebsite") }}
                    </SupsisButton>
                </div>
                <div v-else class="builder-content">
                    <div class="builder-header">
                        <h1 class="page-title">{{ $t("calendar.bookingLandingPageBuilder") }}</h1>
                        <SupsisButton class="preview-button" @click="openPreview">
                            <span class="material-icons">visibility</span>
                            {{ $t("chatbotv2.preview") }}
                        </SupsisButton>
                    </div>
                    <div class="builder-content">
                        <div class="logo-cover-container">
                            <section class="builder-section logo">
                                <h2 class="section-title">{{$t("message.logo")}}</h2>
                                <div class="section-content">
                                    <div
                                        class="logo-container"
                                        @mouseover="showOverlay = true"
                                        @mouseleave="showOverlay = false"
                                        @click="selectLogo"
                                    >
                                        <img v-if="data.logo" :src="getImageUrl(data.logo)" alt="Logo" />
                                        <span v-else class="no-logo">{{$t("message.noLogo")}}</span>
                                        <div v-if="showOverlay" class="overlay">
                                            <i class="material-icons">edit</i>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            <section class="builder-section coverPhoto">
                                <h2 class="section-title">{{ $t("calendar.coverPhoto") }}</h2>
                                <div class="section-content">
                                    <div
                                        class="coverPhoto-container"
                                        @mouseover="showCoverOverlay = true"
                                        @mouseleave="showCoverOverlay = false"
                                        @click="selectCover"
                                    >
                                        <img v-if="data.coverPhoto" :src="getImageUrl(data.coverPhoto)" alt="Kapak Fotoğrafı" />
                                        <span v-else class="no-coverPhoto">{{ $t("calendar.noCoverPhoto") }}</span>
                                        <div v-if="showCoverOverlay" class="overlay">
                                            <i class="material-icons">edit</i>
                                        </div>
                                    </div>
                                </div>
                            </section>
                            <section class="builder-section coverPhoto">
                                <h2 class="section-title">{{$t("message.backgroundPhoto")}}</h2>
                                <div class="section-content">
                                    <div
                                        class="coverPhoto-container"
                                        @mouseover="showBackgroundOverlay = true"
                                        @mouseleave="showBackgroundOverlay = false"
                                        @click="selectBackground"
                                    >
                                        <img
                                            v-if="data.backgroundImage"
                                            :src="getImageUrl(data.backgroundImage)"
                                            alt="Arka Plan Fotoğrafı"
                                        />
                                        <span v-else class="no-coverPhoto">{{ $t("calendar.noCoverPhoto") }}</span>
                                        <div v-if="showBackgroundOverlay" class="overlay">
                                            <i class="material-icons">edit</i>
                                            <i
                                                class="material-icons"
                                                style="color: red"
                                                @click.stop="removeBackgroundImage"
                                                >delete</i
                                            >
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>

                        <section class="builder-section">
                            <h2 class="section-title">{{ $t("calendar.organizationName") }}</h2>
                            <div class="section-content">
                                <input
                                    v-model="data.organizationName"
                                    @change="updateField('organizationName', data.organizationName)"
                                    type="text"
                                    :placeholder="$t('calendar.enterOrganizationName')"
                                />
                            </div>
                        </section>
                        <!-- Yeni About Kısmı -->
                        <section class="builder-section">
                            <h2 class="section-title">
                                {{ $t("calendar.about") }}
                                <span
                                    class="md-icon"
                                    style="cursor: pointer"
                                    v-tippy="{
                                        content: $t('message.whenYouActivateFeature'),
                                    }"
                                    @click="toggleVisibility('about')"
                                >
                                    {{ isSectionVisible("about") ? "disabled_visible" : "visibility" }}
                                </span>
                            </h2>
                            <div class="section-content">
                                <quill-editor
                                    v-model="data.about"
                                    class="quill-editor"
                                    :options="editorOption"
                                    :placeholder="$t('calendar.enterAboutInfo')"
                                />
                            </div>
                        </section>
                        <section class="builder-section">
                            <h2 class="section-title">
                                {{ $t("calendar.service") }}
                                <span
                                    class="md-icon"
                                    style="cursor: pointer"
                                    v-tippy="{
                                       content: $t('message.whenYouActivateFeature'),
                                    }"
                                    @click="toggleVisibility('services')"
                                >
                                    {{ isSectionVisible("services") ? "disabled_visible" : "visibility" }}
                                </span>
                            </h2>
                            <div class="section-content">
                                <div v-for="(service, index) in data.services" :key="index" class="service-group">
                                    <div class="service-header">
                                        <h3
                                            contenteditable="true"
                                            @blur="updateServiceName(index, $event.target.innerText)"
                                            @keydown.enter.prevent="$event.target.blur()"
                                        >
                                            {{ service.groupName }}
                                        </h3>
                                        <SupsisButton danger ghost @click="removeService(index)"
                                            >{{ $t("calendar.deleteServices") }}
                                        </SupsisButton>
                                    </div>
                                    <ul class="pl-5">
                                        <li v-for="plan in service.plans" :key="plan._id">
                                            <span class="d-flex gap-5 align-center">
                                                <i class="material-icons">event</i>
                                                {{ getPlanName(plan) }}
                                            </span>

                                            <SupsisButton ghost danger @click="removePlan(service, plan)">
                                                {{ $t("message.delete") }}
                                            </SupsisButton>
                                        </li>
                                    </ul>
                                    <ul class="pl-5">
                                        <li v-for="userId in service.users" :key="userId">
                                            <span class="d-flex gap-5 align-center">
                                                <img
                                                    v-if="getTeamMember(userId).avatar"
                                                    :src="getAvatarUrl(getTeamMember(userId).avatar)"
                                                    alt="Avatar"
                                                    class="avatar"
                                                />
                                                <span v-else class="no-avatar">{{ $t("calendar.noAvatar") }}</span>
                                                {{ getTeamMember(userId).name }}
                                            </span>
                                            <SupsisButton ghost danger @click="removeUser(service, userId)">
                                                {{ $t("message.delete") }}
                                            </SupsisButton>
                                        </li>
                                    </ul>
                                    <div style="display: flex; gap: 20px">
                                        <CustomPicker
                                            :items="allPlans"
                                            idKey="_id"
                                            labelKey="title"
                                            :title="$t('calendar.selectReservationPlan')"
                                            @input="addPlan(service, $event)"
                                        />
                                        <CustomPicker
                                            :items="data.team"
                                            idKey="_id"
                                            labelKey="name"
                                            :title="$t('message.addTeamMember')"
                                            @input="addUserToService(service, $event)"
                                        />
                                    </div>
                                </div>
                                <SupsisButton primary @click="addService"
                                    >+ {{ $t("calendar.addNewService") }}</SupsisButton
                                >
                            </div>
                        </section>
                        <section class="builder-section">
                            <h2 class="section-title">
                                {{ $t("message.team") }}
                                <span
                                    class="md-icon"
                                    style="cursor: pointer"
                                    v-tippy="{
                                        content: $t('message.whenYouActivateFeature'),
                                    }"
                                    @click="toggleVisibility('team')"
                                >
                                    {{ isSectionVisible("team") ? "disabled_visible" : "visibility" }}
                                </span>
                            </h2>
                            <div class="section-content team-section">
                                <ReservationMemberCard
                                    v-for="(member, index) in data.team"
                                    :key="index"
                                    :member="member"
                                    @edit="editMember(index)"
                                    @delete="deleteMember(index)"
                                    @move-up="moveMemberUp(index)"
                                    @move-down="moveMemberDown(index)"
                                />
                                <div class="add-member-card" @click="addMockMember">
                                    + {{ $t("calendar.addNewMember") }}
                                </div>
                            </div>
                        </section>
                        <section class="builder-section">
                            <h2 class="section-title">
                                {{ $t("message.address") }}
                                <span
                                    class="md-icon"
                                    style="cursor: pointer"
                                    v-tippy="{
                                        content: $t('message.whenYouActivateFeature'),
                                    }"
                                    @click="toggleVisibility('address')"
                                >
                                    {{ isSectionVisible("address") ? "disabled_visible" : "visibility" }}
                                </span>
                            </h2>
                            <div class="section-content">
                                <label for="fullAddress">{{ $t("calendar.fullAddress") }}</label>
                                <input
                                    id="fullAddress"
                                    v-model="data.address.fullAddress"
                                    @change="updateField('address.fullAddress', data.address.fullAddress)"
                                    type="text"
                                    :placeholder="$t('calendar.enterFullAddress')"
                                />
                                <label for="googleMapsUrl">Google Maps URL</label>
                                <input
                                    id="googleMapsUrl"
                                    v-model="data.address.googleMapsUrl"
                                    @change="updateField('address.googleMapsUrl', data.address.googleMapsUrl)"
                                    type="text"
                                    :placeholder="$t('calendar.enterGoogleMapsURL')"
                                />
                                <label for="latitude">{{ $t("calendar.latitude") }}</label>
                                <input
                                    id="latitude"
                                    v-model="data.address.latitude"
                                    @change="updateField('address.latitude', data.address.latitude)"
                                    type="number"
                                    :placeholder="$t('calendar.enterLatitude')"
                                />
                                <label for="longitude">{{ $t("calendar.longitude") }}</label>
                                <input
                                    id="longitude"
                                    v-model="data.address.longitude"
                                    @change="updateField('address.longitude', data.address.longitude)"
                                    type="number"
                                    :placeholder="$t('calendar.enterLongitude')"
                                />
                            </div>
                        </section>
                        <section class="builder-section">
                            <h2 class="section-title">{{ $t("calendar.contactDetails") }}</h2>
                            <div class="section-content">
                                <label for="phone">{{ $t("whatsappChannel.phoneNummer") }}</label>
                                <input
                                    id="phone"
                                    v-model="data.contactInfo.phone"
                                    @change="updateField('contactInfo.phone', data.contactInfo.phone)"
                                    type="text"
                                    :placeholder="$t('calendar.enterPhoneNumber')"
                                />
                                <label for="email">{{ $t("calendar.emailAddress") }}</label>
                                <input
                                    id="email"
                                    v-model="data.contactInfo.email"
                                    @change="updateField('contactInfo.email', data.contactInfo.email)"
                                    type="email"
                                    :placeholder="$t('calendar.enterEmail')"
                                />
                                <label for="website">{{ $t("calendar.websiteURL") }}</label>
                                <input
                                    id="website"
                                    v-model="data.contactInfo.website"
                                    @change="updateField('contactInfo.website', data.contactInfo.website)"
                                    type="text"
                                    :placeholder="$t('calendar.enterWebsiteURL')"
                                />
                            </div>
                        </section>
                        <section class="builder-section">
                            <h2 class="section-title">{{ $t("calendar.reservationPolicy") }}</h2>
                            <div class="section-content">
                                <quill-editor
                                    v-model="data.reservationPolicy"
                                    class="quill-editor"
                                    :options="editorOption"
                                    :placeholder="$t('calendar.enterResPolicy')"
                                />
                            </div>
                        </section>
                        <section class="builder-section working-hours">
                            <h2 class="section-title">{{ $t("calendar.workingHours") }}</h2>
                            <div class="section-content">
                                <div class="rules">
                                    <div>
                                        <span>{{ $t("message.timezone") }}</span>
                                        <a-select
                                            show-search
                                            :placeholder="$t('message.selectTimezone')"
                                            option-filter-prop="children"
                                            style="width: 250px"
                                            :filter-option="filterOption"
                                            @change="changeTimezone"
                                            :value="data.availability.timezone"
                                        >
                                            <a-select-option :value="tz" v-for="(tz, i) in timezones" :key="i">
                                                {{ tz }}
                                            </a-select-option>
                                        </a-select>
                                    </div>
                                    <div v-for="rule in data.availability.rules" :key="rule._id" class="ruleItem">
                                        <a-select
                                            show-search
                                            :placeholder="$t('message.selectDayys')"
                                            option-filter-prop="children"
                                            style="width: 120px"
                                            :filter-option="filterOption"
                                            @change="(e) => handleDayChange(e, rule._id)"
                                            :value="daysHashcode(rule.days)"
                                        >
                                            <a-select-option
                                                :value="daysHashcode(day.value)"
                                                v-for="(day, i) in days"
                                                :key="i"
                                            >
                                                {{ day.label[$i18n.locale] }}
                                            </a-select-option>
                                        </a-select>
                                        <span>From</span>
                                        <a-select
                                            show-search
                                            :placeholder="$t('message.selectStartHour')"
                                            option-filter-prop="children"
                                            style="width: 80px; margin-left: 10px"
                                            :filter-option="filterOption"
                                            @change="(e) => handleFromHourChange(e, rule._id)"
                                            :value="rule.from"
                                        >
                                            <a-select-option :value="hour" v-for="(hour, i) in hours" :key="i">
                                                {{ hour }}
                                            </a-select-option>
                                        </a-select>
                                        <span>To</span>
                                        <a-select
                                            show-search
                                            :placeholder="$t('message.selectEndHour')"
                                            option-filter-prop="children"
                                            style="width: 80px; margin-left: 10px"
                                            :filter-option="filterOption"
                                            @change="(e) => handleEndHourChange(e, rule._id)"
                                            :value="rule.to"
                                        >
                                            <a-select-option :value="hour" v-for="(hour, i) in hours" :key="i">
                                                {{ hour }}
                                            </a-select-option>
                                        </a-select>
                                        <SupsisButton
                                            icon
                                            ghost
                                            danger
                                            xxsmal
                                            @click="deleteHour(rule._id)"
                                            :disabled="data.availability.rules.length === 1"
                                        >
                                            <i class="material-icons">delete</i>
                                        </SupsisButton>
                                    </div>
                                    <a-button class="btnAddHour" type="primary" ghost @click="addHour">
                                        + {{ $t("calendar.addNewHour") }}
                                    </a-button>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
                <Modal2 v-if="editingMemberIndex !== null" @cancel="cancelEdit" @ok="saveEdit" :show-footer="true">
                    <template #title>{{ $t("calendar.updateMemberInfo") }}</template>
                    <div class="member-update-form">
                        <div @click="selectAvatar" class="avatar-upload">
                            <img v-if="editingMember.avatar" :src="getAvatarUrl(editingMember.avatar)" alt="Avatar" />
                            <span v-else>{{ $t("calendar.uploadAvatar") }}</span>
                        </div>
                        <input v-model="editingMember.name" :placeholder="$t('message.name')" />
                        <input v-model="editingMember.title" :placeholder="$t('message.title')" />
                        <quill-editor
                            v-model="editingMember.description"
                            :options="editorOption"
                            :placeholder="$t('message.description')"
                        />
                        <div class="section-content">
                            <div v-for="(plan, index) in editingMember.plans" :key="index" class="plan-group">
                                <div class="plan-header" style="display: flex; justify-content: space-between">
                                    <span class="d-flex gap-5 align-center">
                                        <i class="material-icons">event</i>
                                        {{ getPlanName(plan) }}
                                    </span>
                                    <SupsisButton ghost danger @click="removePlanFromMember(index)">
                                        {{ $t("message.delete") }}
                                    </SupsisButton>
                                </div>
                            </div>
                            <CustomPicker
                                :items="allPlans"
                                idKey="_id"
                                labelKey="title"
                                :title="$t('calendar.selectReservationPlan')"
                                @input="addPlanToMember"
                            />
                        </div>
                    </div>
                </Modal2>
            </div>
        </div>
    </div>
</template>

<script>
import {
    CALENDAR_API,
    createMockReservationLandPage,
    getReservationLandingPage,
    updateReservationLandingPage,
} from "../../../../../api/calendar";
import { uploadAsset } from "../../../../../api/assetUpload";
import mimeTypes from "../../../../../components/util/mimeTypes";
import axios from "axios";
import CustomPicker from "../../../../../components/pickers/CustomPicker.vue";
import SupsisButton from "../../../../../views/Pages/Settings/components/SupsisButton.vue";
import Modal2 from "../../../../../components/common/Modal2.vue";
import ReservationMemberCard from "./ReservationMemberCard.vue";
import moment from "moment-timezone";
import ActionDelete from "../../../../../components/svg/ActionDelete";
import log2 from "../../../../../utils/log2";
import SidebarChild from "../../../../../components/Sidebar/SidebarChild.vue";
import CONFIG from "../../../../../config/config";
import calendarMenuItems from "../calendarMenuItems";

const log = log2("ReservationLandingPageBuilder");
export default {
    name: "ReservationLandingPageBuilder",
    components: {
        CustomPicker,
        SupsisButton,
        ReservationMemberCard,
        Modal2,
        ActionDelete,
        SidebarChild,
    },
    props: {},
    data() {
        return {
            debouncedUpdateField: _.debounce(this.updateField, 1000),
            data: null,
            allPlans: [],
            showOverlay: false,
            showCoverOverlay: false,
            showBackgroundOverlay: false,
            uploading: false,
            uploadProgress: 0,
            loading: true,
            editorOption: {
                theme: "snow",
                modules: {
                    toolbar: [
                        ["bold", "italic", "underline", "strike"],
                        [{ header: [1, 2, 3, 4, 5, 6, false] }],
                        [{ color: [] }, { background: [] }],
                        [{ align: [] }],
                        [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
                        ["link"],
                    ],
                },
            },
            editingMemberIndex: null,
            editingMember: null,
            timezones: moment.tz.names(),
            hours: [
                ...[...Array(48).keys()].map((v, i) =>
                    moment()
                        .startOf("day")
                        .add(v * 30, "minute")
                        .format("HH:mm")
                ),
                "23:59",
            ],
            days: [
                { label: { en: "Monday", tr: "Pazartesi" }, value: [1] },
                { label: { en: "Tuesday", tr: "Salı" }, value: [2] },
                { label: { en: "Wednesday", tr: "Çarşamba" }, value: [3] },
                { label: { en: "Thursday", tr: "Perşembe" }, value: [4] },
                { label: { en: "Friday", tr: "Cuma" }, value: [5] },
                { label: { en: "Saturday", tr: "Cumartesi" }, value: [6] },
                { label: { en: "Sunday", tr: "Pazar" }, value: [0] },
                { label: { en: "Weekdays", tr: "Hafta içi" }, value: [1, 2, 3, 4, 5] },
                { label: { en: "Weekends", tr: "Hafta sonu" }, value: [6, 0] },
                { label: { en: "All Days", tr: "Tüm günler" }, value: [0, 1, 2, 3, 4, 5, 6] },
            ],
            currentPage: "reservationBuilder",
            menuItems: calendarMenuItems,
        };
    },
    methods: {
        async fetchData() {
            try {
                const response = await getReservationLandingPage();
                this.data = response.data;
                const plansResponse = await CALENDAR_API.getReservationPlans();
                this.allPlans = plansResponse;
            } catch (error) {
                console.error(this.$t("calendar.errorRecevingData"), error);
            } finally {
                this.loading = false;
            }
        },
        moveMemberUp(index) {
            if (index > 0) {
                const temp = this.data.team[index - 1];
                this.data.team[index - 1] = this.data.team[index];
                this.data.team[index] = temp;
                this.updateField("team", this.data.team);
            }
        },
        moveMemberDown(index) {
            if (index < this.data.team.length - 1) {
                const temp = this.data.team[index + 1];
                this.data.team[index + 1] = this.data.team[index];
                this.data.team[index] = temp;
                this.updateField("team", this.data.team);
            }
        },
        addService() {
            const newService = {
                groupName: `${this.$t("calendar.anonymService")} ${this.data.services.length + 1}`,
                plans: [],
            };
            this.data.services.push(newService);
            this.updateField("services", this.data.services);
        },
        removeService(index) {
            this.data.services.splice(index, 1);
            this.updateField("services", this.data.services);
        },
        addPlan(service, planId) {
            const plan = this.allPlans.find((p) => p._id === planId);
            if (plan) {
                service.plans.push(plan);
                this.updateField("services", this.data.services);
            }
        },
        getTeamMember(userId) {
            return this.data.team.find((member) => member._id === userId) || {};
        },
        getAvatarUrl(avatar) {
            if (!avatar) return '';
            return avatar.v2Format && avatar.cdnUrl
                ? avatar.cdnUrl
                : `https://static.supsis.live/${avatar.path}${avatar._id}.${avatar.ext}`;
        },
        getImageUrl(image) {
            if (!image) return '';
            return image.v2Format && image.cdnUrl
                ? image.cdnUrl
                : `https://static.supsis.live/${image.path}${image._id}.${image.ext}`;
        },
        removeUser(service, userId) {
            const index = service.users.indexOf(userId);
            if (index > -1) {
                service.users.splice(index, 1);
                this.updateField("services", this.data.services);
            }
        },
        addUserToService(service, memberId) {
            const member = this.data.team.find((m) => m._id === memberId);
            if (member) {
                if (!service.users) {
                    this.$set(service, "users", []);
                }
                if (!service.users.includes(memberId)) {
                    service.users.push(memberId);
                    this.updateField("services", this.data.services);
                }
            }
        },
        removePlan(service, plan) {
            const index = service.plans.indexOf(plan);
            if (index > -1) {
                service.plans.splice(index, 1);
                this.updateField("services", this.data.services);
            }
        },
        getPlanName(plan) {
            const foundPlan = this.allPlans.find((p) => p._id === plan._id);
            return foundPlan ? foundPlan.title : this.$t("calendar.unknownPlan");
        },
        async createMockLandingPage() {
            this.loading = true;
            try {
                await createMockReservationLandPage();
                await this.fetchData();
            } catch (error) {
                console.error(this.$t("calendar.errorCreatingMockPage"), error);
                this.$message.error(this.$t("calendar.failedToCreateBookingPage"));
            } finally {
                this.loading = false;
            }
        },
        async selectLogo() {
            let fileEl = document.createElement("input");
            fileEl.setAttribute("type", "file");
            fileEl.setAttribute("accept", "image/*");

            fileEl.onchange = async () => {
                let file = fileEl.files[0];
                if (file) {
                    this.uploading = true;
                    let path = `${this.$store.state.site.site._id}/reservation-landing-page/`;

                    try {
                        let { data } = await uploadAsset({
                            path,
                            mime: file.type,
                            ext: mimeTypes[file.type] || "png",
                            name: file.name,
                            size: file.size,
                            type: "calendar",
                        });

                        if (file.size <= 10 * 1024 * 1024) {
                            await axios({
                                method: data.method,
                                url: data.url,
                                data: file,
                                headers: {
                                    "Content-Type": file.type,
                                },
                                onUploadProgress: (p) => {
                                    this.uploadProgress = Math.round((p.loaded * 100) / p.total);
                                },
                            });

                            // this.data.logo = `https://static.supsis.live/${data.asset.path}${data.filename}`;
                            await this.updateField("logo", data.asset._id);
                        } else {
                            this.$message.error(this.$t("calendar.failedToCreateBookingPage"));
                        }
                    } catch (error) {
                        console.error(this.$t("calendar.errorLoadingTheLogo"), error);
                        this.$message.error(this.$t("calendar.failedToLoadLogo"));
                    } finally {
                        this.uploading = false;
                    }
                }
            };
            fileEl.click();
        },
        async selectCover() {
            let fileEl = document.createElement("input");
            fileEl.setAttribute("type", "file");
            fileEl.setAttribute("accept", "image/*");

            fileEl.onchange = async () => {
                let file = fileEl.files[0];
                if (file) {
                    this.uploading = true;
                    let path = `${this.$store.state.site.site._id}/reservation-landing-page/`;

                    try {
                        let { data } = await uploadAsset({
                            path,
                            mime: file.type,
                            ext: mimeTypes[file.type] || "png",
                            name: file.name,
                            size: file.size,
                            type: "calendar",
                        });

                        if (file.size <= 10 * 1024 * 1024) {
                            await axios({
                                method: data.method,
                                url: data.url,
                                data: file,
                                headers: {
                                    "Content-Type": file.type,
                                },
                                onUploadProgress: (p) => {
                                    this.uploadProgress = Math.round((p.loaded * 100) / p.total);
                                },
                            });

                            await this.updateField("coverPhoto", data.asset._id);
                        } else {
                            this.$message.error(this.$t("calendar.fileSizeToolLarge"));
                        }
                    } catch (error) {
                        console.error(this.$t("calendar.errorLoadingTheLogo"), error);
                        this.$message.error(this.$t("calendar.failedToLoadLogo"));
                    } finally {
                        this.uploading = false;
                    }
                }
            };
            fileEl.click();
        },
        async selectBackground() {
            let fileEl = document.createElement("input");
            fileEl.setAttribute("type", "file");
            fileEl.setAttribute("accept", "image/*");

            fileEl.onchange = async () => {
                let file = fileEl.files[0];
                if (file) {
                    this.uploading = true;
                    let path = `${this.$store.state.site.site._id}/reservation-landing-page/`;

                    try {
                        let { data } = await uploadAsset({
                            path,
                            mime: file.type,
                            ext: mimeTypes[file.type] || "png",
                            name: file.name,
                            size: file.size,
                            type: "calendar",
                        });

                        if (file.size <= 10 * 1024 * 1024) {
                            await axios({
                                method: data.method,
                                url: data.url,
                                data: file,
                                headers: {
                                    "Content-Type": file.type,
                                },
                                onUploadProgress: (p) => {
                                    this.uploadProgress = Math.round((p.loaded * 100) / p.total);
                                },
                            });

                            await this.updateField("backgroundImage", data.asset._id);
                        } else {
                            this.$message.error(this.$t("calendar.fileSizeToolLarge"));
                        }
                    } catch (error) {
                        console.error(this.$t("calendar.errorLoadingTheLogo"), error);
                        this.$message.error(this.$t("calendar.failedToLoadLogo"));
                    } finally {
                        this.uploading = false;
                    }
                }
            };
            fileEl.click();
        },
        removeBackgroundImage() {
            this.data.backgroundImage = null;
            this.updateField("backgroundImage", null);
        },
        async updateField(field, value) {
            try {
                await updateReservationLandingPage({ [field]: value });
                const fieldParts = field.split(".");
                if (fieldParts.length > 1) {
                    this.data[fieldParts[0]][fieldParts[1]] = value;
                } else {
                    this.data[field] = value;
                }
                this.fetchData();
            } catch (error) {
                console.error(`${field} ${$t("calendar.errorUpdating")}:`, error);
                this.$message.error(`${field} ${$t("calendar.failedToUpdate")}`);
            }
        },
        addMockMember() {
            const newMember = {
                name: this.$t("message.name"),
                title: this.$t("message.title"),
                description: "",
                avatar: null,
            };
            this.data.team.push(newMember);
            this.editingMemberIndex = this.data.team.length - 1;
            this.editingMember = { ...newMember };
        },
        editMember(index) {
            this.editingMemberIndex = index;
            this.editingMember = { ...this.data.team[index] };
        },
        cancelEdit() {
            this.editingMemberIndex = null;
            this.editingMember = null;
        },
        async saveEdit() {
            this.data.team.splice(this.editingMemberIndex, 1, this.editingMember);
            await this.updateField("team", this.data.team);
            this.cancelEdit();
        },
        async selectAvatar() {
            let fileEl = document.createElement("input");
            fileEl.setAttribute("type", "file");
            fileEl.setAttribute("accept", "image/*");

            fileEl.onchange = async () => {
                let file = fileEl.files[0];
                if (file) {
                    this.uploading = true;
                    let path = `${this.$store.state.site.site._id}/reservation-landing-page/`;

                    try {
                        let { data } = await uploadAsset({
                            path,
                            mime: file.type,
                            ext: mimeTypes[file.type] || "png",
                            name: file.name,
                            size: file.size,
                            type: "calendar",
                        });

                        if (file.size <= 10 * 1024 * 1024) {
                            await axios({
                                method: data.method,
                                url: data.url,
                                data: file,
                                headers: {
                                    "Content-Type": file.type,
                                },
                                onUploadProgress: (p) => {
                                    this.uploadProgress = Math.round((p.loaded * 100) / p.total);
                                },
                            });

                            this.editingMember.avatar = data.asset;
                            this.editingMember.avatar.url = data.asset.v2Format && data.asset.cdnUrl
                                ? data.asset.cdnUrl
                                : `https://static.supsis.live/${data.asset.path}${data.asset._id}.${data.asset.ext}`;
                            this.data.team = this.data.team.map((member, i) => {
                                if (i === this.editingMemberIndex) {
                                    return this.editingMember;
                                }
                                return member;
                            });
                            this.updateField("team", this.data.team);
                            this.fetchData();
                        } else {
                            this.$message.error(this.$t("calendar.fileSizeToolLarge"));
                        }
                    } catch (error) {
                        console.error(this.$t("calendar.errorLoadingAvatar"), error);
                        this.$message.error(this.$t("calendar.failedToLoadAvatar"));
                    } finally {
                        this.uploading = false;
                    }
                }
            };
            fileEl.click();
        },
        async changeTimezone(timezone) {
            log("changeTimezone", { timezone });
            this.data.availability.timezone = timezone;
            await this.updateField("availability.timezone", timezone);
        },
        async handleDayChange(value, ruleId) {
            log("handleDayChange", { value, ruleId });
            const rule = this.data.availability.rules.find((r) => r._id === ruleId);
            const newDays = this.days.find((day) => this.daysHashcode(day.value) === value).value;
            if (JSON.stringify(rule.days) !== JSON.stringify(newDays)) {
                rule.days = newDays;
                await this.updateField("availability.rules", this.data.availability.rules);
            }
        },
        async handleFromHourChange(value, ruleId) {
            log("handleFromHourChange", { value, ruleId });
            const rule = this.data.availability.rules.find((r) => r._id === ruleId);
            rule.from = value;
            await this.updateField("availability.rules", this.data.availability.rules);
        },
        async handleEndHourChange(value, ruleId) {
            log("handleEndHourChange", { value, ruleId });
            const rule = this.data.availability.rules.find((r) => r._id === ruleId);
            rule.to = value;
            await this.updateField("availability.rules", this.data.availability.rules);
        },
        async deleteHour(id) {
            log("deleteHour", { id });
            this.data.availability.rules = this.data.availability.rules.filter((x) => x._id !== id);
            await this.updateField("availability.rules", this.data.availability.rules);
        },
        async addHour() {
            log("addHour");
            const newRule = {
                _id: Date.now().toString(),
                days: [1, 2, 3, 4, 5],
                from: "09:00",
                to: "17:00",
            };
            this.data.availability.rules.push(newRule);
            await this.updateField("availability.rules", this.data.availability.rules);
        },
        daysHashcode(days) {
            log("daysHashcode", { days });
            days = JSON.parse(JSON.stringify(days));
            return _.orderBy(days).join("-");
        },
        filterOption(input, option) {
            return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
        fnClicked(item) {
            if (item.key === "calendar") {
                this.$router.push("/calendar");
            } else if (item.key === "reservationBuilder") {
                // Zaten bu sayfadayız, bir şey yapmaya gerek yok
            }
        },
        openPreview() {
            const previewURL = `${CONFIG.CALENDAR_URL}/?siteId=${this.$store.state.site.site._id}&userId=${this.$store.state.auth.user._id}`;
            window.open(previewURL, "_blank");
        },
        async updateServiceName(index, newName) {
            if (this.data.services[index].groupName !== newName) {
                this.data.services[index].groupName = newName;
                await this.updateField("services", this.data.services);
            }
        },
        async deleteMember(index) {
            try {
                this.data.team.splice(index, 1);
                await this.updateField("team", this.data.team);
                this.$message.success(this.$t("calendar.teamMemberSuccessfullyDeleted"));
            } catch (error) {
                console.error(this.$t("calendar.teamMemberDeleteError"), error);
                this.$message.error(this.$t("calendar.teamMemberNotDelete"));
            }
        },
        toggleVisibility(section) {
            const index = this.data.landingPageVisibility.indexOf(section);
            if (index === -1) {
                this.data.landingPageVisibility.push(section);
            } else {
                this.data.landingPageVisibility.splice(index, 1);
            }
            this.updateField("landingPageVisibility", this.data.landingPageVisibility);
        },
        isSectionVisible(section) {
            return this.data.landingPageVisibility.includes(section);
        },
        addPlanToMember(planId) {
            const plan = this.allPlans.find((p) => p._id === planId);
            if (plan) {
                if (!this.editingMember) {
                    this.editingMember = {};
                }
                if (!this.editingMember.plans) {
                    this.editingMember.plans = [];
                }
                this.editingMember.plans.push(plan);
                this.updateField("team", this.data.team);
            }
        },
        removePlanFromMember(index) {
            this.editingMember.plans.splice(index, 1);
            this.updateField("team", this.data.team);
        },
    },
    mounted() {
        this.fetchData();
    },
    watch: {
        "data.about": function (newVal) {
            this.debouncedUpdateField("about", newVal);
        },
        "data.services": {
            handler(newVal) {
                // this.debouncedUpdateField('services', newVal);
            },
            deep: true,
        },
        "data.reservationPolicy": function (newVal) {
            this.debouncedUpdateField("reservationPolicy", newVal);
        },
    },
};
</script>

<style scoped lang="scss">
.reservation-landing-page-builder {
    .route-page-content {
        height: 100% !important;
    }

    .page-title {
        font-size: 2em;
        margin-bottom: 20px;
        text-align: center;
    }

    .builder-content {
        max-width: 1200px;
        margin: 0 auto;
        padding: 20px;
        background-color: #f9f9f9;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .logo-cover-container {
        display: flex;
        justify-content: space-between;
        margin-bottom: 20px;
    }

    .builder-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
    }

    .preview-button {
        display: flex;
        align-items: center;
        gap: 5px;
        padding: 10px 15px;
        background-color: #4caf50;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        transition: background-color 0.3s;

        &:hover {
            background-color: #45a049;
        }

        .material-icons {
            font-size: 18px;
        }
    }
}

.loading,
.no-data {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 300px;
    font-size: 1.2em;
}

.create-button {
    padding: 10px 20px;
    font-size: 1em;
    background-color: #4caf50;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s;

    &:hover {
        background-color: #45a049;
    }
}

.builder-section {
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;

    .section-title {
        font-size: 1.5em;
        margin-bottom: 10px;
    }

    .section-content {
        input {
            width: 100%;
            padding: 10px;
            font-size: 1em;
            border: 1px solid #ccc;
            border-radius: 4px;
        }
    }

    &.logo {
        margin-bottom: 20px;

        .logo-container {
            width: 200px;
            height: 200px;
            border: 2px dashed #ccc;
            display: flex;
            justify-content: center;
            align-items: center;
            position: relative;
            cursor: pointer;

            img {
                max-width: 100%;
                max-height: 100%;
                object-fit: contain;
            }

            .no-logo {
                color: #999;
            }

            .overlay {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-color: rgba(0, 0, 0, 0.5);
                display: flex;
                justify-content: center;
                align-items: center;

                i {
                    color: white;
                    font-size: 24px;
                }
            }
        }
    }

    &.coverPhoto {
        margin-bottom: 20px;

        .coverPhoto-container {
            width: 100%;
            height: 200px; /* Geniş dikdörtgen */
            border: 2px dashed #ccc;
            display: flex;
            justify-content: center;
            align-items: center;
            position: relative;
            cursor: pointer;

            img {
                max-width: 100%;
                max-height: 100%;
                object-fit: coverPhoto;
            }

            .no-coverPhoto {
                color: #999;
            }

            .overlay {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-color: rgba(0, 0, 0, 0.5);
                display: flex;
                justify-content: center;
                align-items: center;

                i {
                    color: white;
                    font-size: 24px;
                }
            }
        }
    }

    .service-group {
        margin-bottom: 20px;
        padding: 10px;
        border: 1px solid #ccc;
        border-radius: 4px;

        .service-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            background-color: #f0f0f0;
            padding: 10px;
            margin-bottom: 10px;
            border-bottom: 2px solid #ccc;

            h3 {
                font-size: 1.2em;
                margin: 0;
                min-width: 100px;
                padding: 5px;
                border: 1px solid transparent;
                transition: border-color 0.3s;

                &:hover,
                &:focus {
                    border-color: #ccc;
                    outline: none;
                }
            }
        }

        ul {
            list-style: none;
            padding-left: 20px;
            border-bottom: 1px solid #ccc;

            li {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 5px;
                padding-bottom: 5px;
                border-bottom: 1px solid #eee;

                &:last-child {
                    border-bottom: none;
                }

                button {
                    background-color: #f44336;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    padding: 5px 10px;

                    &:hover {
                        background-color: #d32f2f;
                    }
                }
            }
        }

        button {
            background-color: #4caf50;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            padding: 10px 20px;
            margin-top: 10px;

            &:hover {
                background-color: #45a049;
            }
        }
    }

    .team-section {
        display: flex;
        flex-wrap: wrap;
        gap: 10px; // Aralarına boşluk ekle

        .add-member-card {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 200px;
            height: 200px;
            border: 2px dashed #ccc;
            cursor: pointer;
        }
    }

    &.working-hours {
        .rules {
            .ruleItem {
                display: flex;
                align-items: center;
                gap: 5px;
                padding: 5px;
            }
        }
    }
}

.member-update-form {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .avatar-upload {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100px;
        height: 100px;
        border: 2px dashed #ccc;
        cursor: pointer;
        margin-bottom: 10px;

        img {
            max-width: 100%;
            max-height: 100%;
            object-fit: cover;
        }

        span {
            color: #999;
        }
    }

    input {
        padding: 10px;
        font-size: 1em;
        border: 1px solid #ccc;
        border-radius: 4px;
    }

    .quill-editor {
        //height: 200px;
    }
}

.ant-modal-root.v2 {
    .ant-modal-close-x {
        position: absolute;
        color: white;
        font-size: 20px;
        width: 30px;
        height: 30px;
        line-height: 30px;
        border-radius: 4px;

        &:hover {
            background: var(--color-bg-ghost-hover);
        }
    }

    .ant-modal-body {
        padding: 0 !important;
        font-size: 14px;
        font-weight: 400;
        color: var(--color-text-default);
        line-height: 24px;
        max-height: 70vh; // Limit the height of the modal body
        overflow-y: auto; // Enable vertical scrolling if content overflows
    }

    .ant-modal-content {
        border-radius: 4px;
    }

    .custom-modal-content {
        display: flex;
        flex-direction: column;

        .custom-modal-header {
            display: flex;
            flex-direction: column;
            gap: 10px;
            padding: 20px;
            border-bottom: 1px solid var(--color-border-default);

            font-weight: 700;
            color: var(--color-text-strong-default);
            font-size: 24px;
            line-height: 32px;

            .title {
                font-weight: 700;
                color: var(--color-text-strong-default);
                font-size: 24px;
                line-height: 32px;
            }
        }

        .custom-modal-body {
            display: flex;
            flex-direction: column;
            gap: 10px;
            padding: 20px 40px;
            background: var(--color-bg-neutral-default);
        }

        .custom-modal-footer {
            border-top: 1px solid var(--color-border-default);
            padding: 20px;

            .actions {
                display: flex;
                gap: 5px;
                width: 100%;
                justify-content: flex-end;
            }
        }
    }
}
.avatar {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    object-fit: cover;
}
.no-avatar {
    color: #999;
}
</style>
